import { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Paperclip, X, File, Image, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FileUpload = ({ conversationId, onFileUploaded }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Restrict to images: jpg, png, webp
    const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!ALLOWED.includes(file.type)) {
      toast.error('Format non supporté', { description: 'Seules les images JPG, PNG ou WEBP sont acceptées.' });
      return;
    }

    // Validate file size (5MB max for images)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image trop lourde', { description: "L'image ne doit pas dépasser 5 Mo." });
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/conversations/${conversationId}/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Fichier envoyé');
      
      if (onFileUploaded) {
        onFileUploaded(response.data);
      }

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erreur', { description: 'Impossible d\'envoyer le fichier' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelSelection = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = () => {
    if (!selectedFile) return <File className="w-5 h-5" />;
    
    if (selectedFile.type.startsWith('image/')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    } else if (selectedFile.type === 'application/pdf') {
      return <FileText className="w-5 h-5 text-red-500" />;
    } else {
      return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="relative">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/jpeg,image/png,image/webp"
        data-testid="chat-file-input"
      />

      {!selectedFile ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="text-gray-500 hover:text-gray-700"
          data-testid="chat-attach-image-button"
          title="Joindre une image (JPG, PNG, WEBP — 5 Mo max)"
        >
          <Paperclip className="w-5 h-5" />
        </Button>
      ) : (
        <div className="absolute bottom-full left-0 mb-2 bg-white border rounded-lg shadow-lg p-3 min-w-64">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              {getFileIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button
              onClick={handleCancelSelection}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              onClick={handleUpload}
              disabled={isUploading}
              className="flex-1"
            >
              {isUploading ? 'Envoi...' : 'Envoyer'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancelSelection}
              disabled={isUploading}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
